import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { EmployeeLayout } from '../../components/EmployeeLayout';
import { employeeAPI, restaurantAPI, shiftReplacementAPI } from '../../lib/api';import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import CalendarSyncButtons from '../../components/CalendarSyncButtons';
import ShiftDetailsModal from '../../components/ShiftDetailsModal';
import RequestReplacementModal from '../../components/RequestReplacementModal';

function getMondayOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function fmtDate(d) {
  return d.toISOString().split('T')[0];
}

function fmtDisplay(d) {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isToday(d) {
  return fmtDate(d) === fmtDate(new Date());
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function EmployeeSchedule() {
  const { user } = useAuth();
  const [weekMonday, setWeekMonday] = useState(() => getMondayOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSync, setShowSync] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  // Employee-initiated coverage requests
  const [coverageRequests, setCoverageRequests] = useState([]);
  const [showReplacementModal, setShowReplacementModal] = useState(false);

  const loadCoverageRequests = useCallback(() => {
    shiftReplacementAPI
      .employeeRequestsList('mine')
      .then((res) => setCoverageRequests(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCoverageRequests([]));
  }, []);

  useEffect(() => { loadCoverageRequests(); }, [loadCoverageRequests]);

  const calendarUrl = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/employee/${user?.id}.ics`;

  useEffect(() => {
    if (!user?.restaurant_id) {
      setLoading(false);
      return;
    }
    const start = fmtDate(weekMonday);
    const end = fmtDate(addDays(weekMonday, 6));
    setLoading(true);
    employeeAPI
      .getSchedule(user.restaurant_id, { start_date: start, end_date: end })
      .then(res => setShifts(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [weekMonday, user]);

  // Fetch restaurant once for the modal — used by ShiftDetailsModal for
  // property/address/GPS info. Failure is non-fatal: the modal still opens
  // with the shift fields it already has.
  useEffect(() => {
    if (!user?.restaurant_id) return;
    restaurantAPI
      .getOne(user.restaurant_id)
      .then(res => setRestaurant(res.data || null))
      .catch(() => setRestaurant(null));
  }, [user?.restaurant_id]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekMonday, i));
  const weekEnd = addDays(weekMonday, 6);

  // Map shift_id -> latest coverage request for badge state
  const coverageByShift = React.useMemo(() => {
    const map = {};
    for (const r of coverageRequests) {
      if (!r.shift_id) continue;
      // Prefer pending over approved over rejected; otherwise newest wins
      const order = { pending_manager_approval: 3, manager_approved: 2, manager_rejected: 1 };
      const cur = map[r.shift_id];
      if (!cur || (order[r.status] || 0) > (order[cur.status] || 0)) {
        map[r.shift_id] = r;
      }
    }
    return map;
  }, [coverageRequests]);

  // Can the staff request a replacement on this shift? Future + not cancelled
  // + no open request yet. Past + completed self-excluded by future check.
  const canRequestReplacementFor = (s) => {
    if (!s) return false;
    if (String(s.status || '').toLowerCase() === 'cancelled') return false;
    try {
      const start = new Date(`${s.date}T${(s.start_time || '00:00')}:00`);
      if (Number.isNaN(start.getTime())) return false;
      if (start <= new Date()) return false; // future-only
    } catch {
      return false;
    }
    const open = coverageByShift[s.id];
    if (open && open.status === 'pending_manager_approval') return false;
    return true;
  };

  const replacementRequestedFor = (s) => {
    const r = coverageByShift[s?.id];
    return r && r.status === 'pending_manager_approval';
  };

  return (
    <EmployeeLayout>
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden" data-testid="employee-schedule">
        <div className="space-y-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            My Schedule
          </h1>
          {/* Controls row — wraps cleanly on small screens */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              onClick={() => setShowSync(v => !v)}
              data-testid="sync-calendar-btn"
              className="flex items-center gap-2 px-3 min-h-[44px] rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <CalendarDays size={15} className="text-orange-500" />
              <span>Sync with Calendar</span>
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeekMonday(getMondayOfWeek(addDays(weekMonday, -7)))}
                className="h-11 w-11 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid="prev-week-btn"
                aria-label="Previous week"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm font-medium text-slate-700 whitespace-nowrap px-1">
                {fmtDisplay(weekMonday)} – {fmtDisplay(weekEnd)}
              </span>
              <button
                onClick={() => setWeekMonday(getMondayOfWeek(addDays(weekMonday, 7)))}
                className="h-11 w-11 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid="next-week-btn"
                aria-label="Next week"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Sync Panel */}
        {showSync && (
          <CalendarSyncButtons calendarUrl={calendarUrl} />
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 gap-2 sm:gap-3">
            {days.map((day, idx) => {
              const dayStr = fmtDate(day);
              const dayShifts = shifts.filter(s => s.date === dayStr);
              const today = isToday(day);
              return (
                <div
                  key={dayStr}
                  className={`rounded-xl border p-3 md:min-h-[120px] ${
                    today ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white'
                  }`}
                >
                  {/* Mobile: horizontal "MON 18" + shifts inline; Desktop: vertical layout */}
                  <div className="flex items-baseline gap-2 md:block">
                    <p className={`text-xs font-semibold uppercase tracking-wide ${today ? 'text-orange-600' : 'text-slate-400'}`}>
                      {DAYS[idx]}
                    </p>
                    <p className={`text-lg md:text-xl font-bold md:mt-0.5 ${today ? 'text-orange-600' : 'text-slate-900'}`}>
                      {day.getDate()}
                    </p>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {dayShifts.length === 0 ? (
                      <p className="text-slate-400 text-xs">Off</p>
                    ) : (
                      dayShifts.map(s => {
                        const isRequested = replacementRequestedFor(s);
                        return (
                          <button
                            type="button"
                            key={s.id}
                            onClick={() => {
                              setSelectedShift(s);
                              employeeAPI.markOnboardingEvent('shift_opened').catch(() => {});
                            }}
                            className="block w-full text-left bg-slate-900 text-white rounded-lg px-3 py-2 text-sm md:text-xs min-h-[44px] hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 transition-colors"
                            data-testid={`employee-shift-card-${s.id}`}
                            aria-label={`Open shift details for ${s.start_time} to ${s.end_time}`}
                          >
                            <p className="font-medium whitespace-nowrap">{s.start_time} – {s.end_time}</p>
                            {s.role_name && (
                              <p className="text-slate-300 text-xs mt-0.5 truncate">{s.role_name}</p>
                            )}
                            {isRequested && (
                              <p
                                className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-orange-300 bg-orange-500/15 border border-orange-400/30 rounded-full px-1.5 py-0.5"
                                data-testid={`shift-replacement-badge-${s.id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                                Replacement Requested
                              </p>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && shifts.length === 0 && (
          <p className="text-center text-slate-400 text-sm py-4">
            No published shifts for this week.
          </p>
        )}
      </div>

      <ShiftDetailsModal
        open={!!selectedShift}
        onClose={() => setSelectedShift(null)}
        shift={selectedShift}
        restaurant={restaurant}
        canRequestReplacement={canRequestReplacementFor(selectedShift)}
        replacementRequested={replacementRequestedFor(selectedShift)}
        onRequestReplacement={() => setShowReplacementModal(true)}
      />

      <RequestReplacementModal
        open={showReplacementModal}
        onClose={() => setShowReplacementModal(false)}
        shift={selectedShift}
        onSubmitted={() => {
          loadCoverageRequests();
          setSelectedShift(null);
        }}
      />
    </EmployeeLayout>
  );
}
